import { CSSProperties } from '@material-ui/core/styles/withStyles';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import clsx from 'clsx';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { DeviceConfigFirmwareSchema } from '@moderepo/mode-apis';
import { SlideOutPanelLocation, showSlideOutPanel, modeUIContext } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { DeviceConfigSchemasTableComp, DeviceConfigSchemasTableCompDataItem, DeviceConfigSchemasTableCompProps } from '../../components/devices';
import { useSelectDeviceConfigSchemas } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { DeviceConfigPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';



export interface DeviceConfigSchemasListingContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    readonly projectId: number;
    readonly deviceId: number;

    // Only available if the agent is looking at a user's device
    readonly userId?: number | undefined;
    readonly homeId?: number | number;
}


/**
 * This component is used for displaying a list of device config schemas in Table view. This will only show the list of schemas, not the
 * actual device configuration.
 */
export const DeviceConfigSchemasListingContainer: React.FC<DeviceConfigSchemasListingContainerProps> = (
    props: DeviceConfigSchemasListingContainerProps,
) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const deviceConfigSchemas = useSelectDeviceConfigSchemas(props.deviceId, false, true, true);

    const { trans } = useRitzTranslation(project);
    const [selectedFirmwareSchema, setSelectedFirmwareSchema] = useState<DeviceConfigFirmwareSchema>();


    /**
     * Create the table columns for the DeviceConfigSchemasTableComp and cache it.
     */
    const deviceConfigSchemasTableFieldSettings = useMemo((): DeviceConfigSchemasTableCompProps['fieldsSettings'] => {
        return {
            fields: {
                componentName: {
                    label: capitalizeAll(trans('device_config.label.firmware_component_name')),
                },
                componentVersionName: {
                    label: capitalizeAll(trans('device_config.label.firmware_component_version_name')),
                },
            },
        };
    }, [trans]);



    /**
     * Once we fetched the device's config schemas, convert them to the date structure that the DeviceConfigSchemasTableComp requires. We
     * will pass this data to the DeviceConfigSchemasTableComp for rendering
     */
    const deviceConfigSchemasTableData = useMemo((): DeviceConfigSchemasTableCompProps['listData'] => {
        return deviceConfigSchemas?.map((firmwareSchema): DeviceConfigSchemasTableCompDataItem => {
            return {
                actualValue: firmwareSchema,
                selected   : selectedFirmwareSchema === firmwareSchema,
            };
        });
    }, [deviceConfigSchemas, selectedFirmwareSchema]);


    /**
     * When a firmware component schema is selected, we will open the preview panel to show the device config based on the schema
     */
    const onFirmwareCompSchemaSelected = useCallback((firmwareCompSchema: DeviceConfigFirmwareSchema) => {
        setSelectedFirmwareSchema(firmwareCompSchema);

        // Dispatch an action to show the preview panel
        const options: DeviceConfigPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_CONFIG_PREVIEW,
            compProps: {
                data: {
                    projectId                   : props.projectId,
                    deviceId                    : props.deviceId,
                    firmwareComponentName       : firmwareCompSchema.componentName,
                    firmwareComponentVersionName: firmwareCompSchema.componentVersionName,
                },
                context: {
                    userId: props.userId,
                    homeId: props.homeId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.deviceId, props.homeId, props.projectId, props.userId, uiDispatch]);


    return (
        <DeviceConfigSchemasTableComp
            className={clsx(props.className)}
            title={capitalizeAll(trans('label.device_config', { count: 2 }))}
            icon="settings"
            fieldsSettings={deviceConfigSchemasTableFieldSettings}
            listData={deviceConfigSchemasTableData}
            emptyDataMessageComp={(
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.device_config_schema', { count: 2 })),
                    })}
                />
            )}
            onListItemSelected={onFirmwareCompSchemaSelected}
        />
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('DeviceConfigSchemasListingContainer', DeviceConfigSchemasListingContainer);
